import { useState } from 'react';
import { X, Save } from 'lucide-react';

interface SaveLayoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string) => void;
}

export function SaveLayoutModal({ isOpen, onClose, onSave }: SaveLayoutModalProps) {
  const [layoutName, setLayoutName] = useState('');
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSave = () => {
    // Validate layout name (Requirements 6.1)
    const trimmedName = layoutName.trim();
    if (!trimmedName) {
      setError('Layout name cannot be empty');
      return;
    }
    if (trimmedName.length < 2) {
      setError('Layout name must be at least 2 characters');
      return;
    }
    if (trimmedName.length > 50) {
      setError('Layout name must be less than 50 characters');
      return;
    }

    // Save layout (Requirements 6.3)
    onSave(trimmedName);
    
    // Reset and close
    setLayoutName('');
    setError('');
    onClose();
  };

  const handleCancel = () => {
    setLayoutName('');
    setError('');
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-[var(--daw-bg-elevated)] border border-[var(--daw-border)] rounded-lg shadow-2xl w-[500px] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--daw-border)]">
          <div className="flex items-center gap-2">
            <Save className="w-5 h-5 text-[var(--daw-accent-primary)]" />
            <h2 className="text-lg font-semibold text-white">Save Current Layout</h2>
          </div>
          <button
            onClick={handleCancel}
            className="p-1 hover:bg-[var(--daw-bg-tertiary)] rounded transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-4">
            <label htmlFor="layout-name" className="block text-sm font-medium text-gray-300 mb-2">
              Layout Name
            </label>
            <input
              id="layout-name"
              type="text"
              value={layoutName}
              onChange={(e) => {
                setLayoutName(e.target.value);
                setError('');
              }}
              onKeyDown={handleKeyDown}
              placeholder="e.g., My Custom Layout"
              className="w-full px-4 py-2 bg-[var(--daw-bg-secondary)] border border-[var(--daw-border)] rounded text-white placeholder-gray-500 focus:outline-none focus:border-[var(--daw-accent-primary)] transition-colors"
              autoFocus
            />
            {error && (
              <p className="mt-2 text-sm text-red-400">{error}</p>
            )}
          </div>

          <div className="text-sm text-gray-400 mb-4">
            <p>This will save your current workspace configuration including:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Browser panel visibility</li>
              <li>Mixer height</li>
              <li>Channel rack height</li>
              <li>Panel sizes</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-[var(--daw-bg-secondary)] border-t border-[var(--daw-border)]">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-[var(--daw-bg-tertiary)] rounded transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-[var(--daw-accent-primary)] hover:bg-[var(--daw-accent-hover)] rounded transition-colors flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            Save Layout
          </button>
        </div>
      </div>
    </div>
  );
}
